<script setup>
import {Filter} from "lucide-vue-next";
import {computed} from "vue";

const props = defineProps({
    currentView: {
        type: String,
    },
    currentSort: {
        type: String,
    },
    isUserLoggedIn: {
        type: Boolean,
    },
    userSponsoredCount: {
        type: Number,
    },
    releasedCount: {
        type: Number,
    },
    totalCount: {
        type: Number,
    },
    sponsoredCount: {
        type: Number,
        default: 0,
    },
    allCount: {
        type: Number,
        default: 0,
    },
    exploreCount: {
        type: Number,
        default: 0,
    },
    sortOptions: {
        type: Object
    },
    searchQuery: {
        type: String,
        default: ''
    },
    filteredSponsoredCount: {
        type: Number,
        default: null
    },
    filteredReleasedCount: {
        type: Number,
        default: null
    },
    filteredMineCount: {
        type: Number,
        default: null
    },
    filteredAllCount: {
        type: Number,
        default: null
    },
    filteredExploreCount: {
        type: Number,
        default: null
    },
    castingsCount: {
        type: Number,
        default: 0
    }
})

defineEmits(['updateCurrentView', 'updateCurrentSort'])

const viewLabel = computed(() => {
    if (props.currentView === 'mine') return 'sponsored'
    if (props.currentView === 'released') return 'released'
    if (props.currentView === 'sponsored') return 'sponsored'
    if (props.currentView === 'castings') return 'pitches'
    return 'active'
})

const displaySponsoredCount = computed(() => {
    return props.searchQuery && props.filteredSponsoredCount !== null
        ? props.filteredSponsoredCount
        : props.sponsoredCount
})

const displayReleasedCount = computed(() => {
    return props.searchQuery && props.filteredReleasedCount !== null
        ? props.filteredReleasedCount
        : props.releasedCount
})

const displayMineCount = computed(() => {
    return props.searchQuery && props.filteredMineCount !== null
        ? props.filteredMineCount
        : props.userSponsoredCount
})

const displayAllCount = computed(() => {
    return props.searchQuery && props.filteredAllCount !== null
        ? props.filteredAllCount
        : props.allCount
})

const displayExploreCount = computed(() => {
    return props.searchQuery && props.filteredExploreCount !== null
        ? props.filteredExploreCount
        : props.exploreCount
})

const filteredCount = computed(() => {
    if (props.currentView === 'all') {
        return displayAllCount.value;
    }
    if (props.currentView === 'explore') {
        return displayExploreCount.value;
    }
    if (props.currentView === 'mine') {
        return displayMineCount.value;
    }
    if (props.currentView === 'released') {
        return displayReleasedCount.value;
    }
    if (props.currentView === 'sponsored') {
        return displaySponsoredCount.value;
    }
    if (props.currentView === 'castings') {
        return props.castingsCount;
    }
    return props.totalCount
})

const tabs = computed(() => [
    { value: 'all', label: 'All', count: displayAllCount.value, badgeClass: 'bg-gray-500' },
    { value: 'explore', label: 'Explore', count: displayExploreCount.value, badgeClass: 'bg-gray-500' },
    { value: 'sponsored', label: 'Sponsored', count: displaySponsoredCount.value, badgeClass: 'bg-blue-600' },
    { value: 'released', label: 'Released', count: displayReleasedCount.value, badgeClass: 'bg-emerald-600' },
    { value: 'mine', label: 'Mine', count: displayMineCount.value, badgeClass: 'bg-red-600', requiresAuth: true },
    { value: 'castings', label: 'Pitches', count: props.castingsCount, badgeClass: 'bg-purple-600' },
].filter(tab => !tab.requiresAuth || props.isUserLoggedIn))

const tabsWithFilters = ['all', 'explore']
</script>

<template>
    <div class="hidden lg:block sticky top-[63px] z-40 bg-zinc-900/95 backdrop-blur-xl border-b border-zinc-800">
        <div class="max-w-[1400px] mx-auto px-6 py-4 space-y-3">
            <!-- Tabs and Sorting Row -->
            <div class="flex items-center justify-between gap-6">
                <div class="flex items-center gap-4 min-w-0">
                    <div class="inline-flex rounded-2xl bg-white/[0.06] border border-white/10 p-1">
                        <button
                            v-for="tab in tabs"
                            :key="tab.value"
                            @click="$emit('updateCurrentView', tab.value)"
                            :class="[
                                'h-10 px-4 rounded-xl text-sm font-semibold transition flex items-center gap-2',
                                currentView === tab.value
                                    ? 'bg-white/10 text-white shadow-sm'
                                    : 'text-white/60 hover:text-white hover:bg-white/[0.06]'
                            ]"
                        >
                            {{ tab.label }}
                            <span
                                v-if="tab.count > 0"
                                :class="['min-w-[1.25rem] h-5 px-2 rounded-full text-white text-xs font-bold flex items-center justify-center', tab.badgeClass]"
                            >
                                {{ tab.count }}
                            </span>
                        </button>
                    </div>

                    <span class="text-sm text-white/35 truncate">
                        {{ filteredCount }}
                        {{ viewLabel }}
                      </span>
                </div>

                <div class="flex items-center gap-3 shrink-0">
                    <!-- Sort Dropdown -->
                    <template v-if="tabsWithFilters.includes(currentView)">
                        <Filter class="w-4 h-4 text-white/35" />
                        <span class="text-sm text-white/35">Sort</span>
                        <select
                            :value="currentSort"
                            @change="$emit('updateCurrentSort', $event.target.value)"
                            class="h-10 bg-white/[0.06] border border-white/10 focus:border-none text-white text-sm font-semibold rounded-xl px-3 hover:bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-auditionsBlue/60 cursor-pointer transition"
                        >
                            <option v-for="(label, value) in sortOptions" :value="value" :key="value">{{ label }}</option>
                        </select>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
